import ProductPrice, { ProductPriceStyles } from "@husky-hrc/Overrides/Components/ProductPrice";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import translate from "@insite/client-framework/Translate";
import { PromotionModel } from "@insite/client-framework/Types/ApiModels";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import CartLineQuantity, { CartLineQuantityStyles } from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    promotions?: PromotionModel[];
    editable: boolean;
    showSavingsAmount: boolean;
    showInvalidPriceMessage?: boolean;
    extendedStyles?: CheckoutCartLineCardCondensedStyles;
}

type Props = OwnProps & HasCartLineContext;

export interface CheckoutCartLineCardCondensedStyles {
    container?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    orderLineInfoGridItem?: GridItemProps;
    orderLineInfoContainer?: GridContainerProps;
    infoLeftColumn?: GridItemProps;
    infoLeftColumnContainer?: GridContainerProps;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productBrand?: ProductBrandStyles;
    productDescription?: ProductDescriptionStyles;
    productErpNumberText?: TypographyPresentationProps;
    productPriceAndQuantityGridItem?: GridItemProps;
    productPriceAndQuantityContainer?: GridContainerProps;
    priceGridItem?: GridItemProps;
    price?: ProductPriceStyles;
    priceHeadingAndText?: SmallHeadingAndTextStyles;
    quantityGridItem?: GridItemProps;
    quantity?: CartLineQuantityStyles;
    extendedUnitNetPriceGridItem?: GridItemProps;
    extendedUnitNetPrice?: SmallHeadingAndTextStyles;
}

export const checkoutCartLineCardCondensedStyles: CheckoutCartLineCardCondensedStyles = {
    container: {
        gap: 20,
        css: css`
            padding: 1rem 0;
        `,
    },
    productImageGridItem: {
        width: 5,
    },
    orderLineInfoGridItem: {
        width: 7,
        css: css`
            padding-top: 0px;
        `,
    },
    orderLineInfoContainer: {
        gap: 20,
    },
    infoLeftColumn: {
        width: 12,
        css: css`
            display: flex;
            flex-direction: column;
            padding-top: 0px;
        `,
    },
    infoLeftColumnContainer: {
        gap: 10,
    },
    productBrandAndDescriptionGridItem: {
        css: css`
            flex-direction: column;
        `,
        width: 12,
    },

    productDescription: {
        productDetailLink: {
            typographyProps: {
                forwardAs: "h3",
            },
            color: "#3a3a3a",
            css: css`
                letter-spacing: 1px;
                font-size: 18px;
                font-weight: 600;
                text-decoration: none;
                line-height: 32px;
                outline: none;
            `,
        },
    },

    productPriceAndQuantityGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    productPriceAndQuantityContainer: {
        gap: 10,
    },
    priceGridItem: {
        width: 5,
    },
    price: {
        price: {
            priceText: {
                weight: 600,
                size: 16,
            },
        },
    },
    quantityGridItem: {
        width: 3,
    },

    quantity: {
        readOnlyQuantityText: {
            heading: {
                css: css`
                    font-size: 15px;
                    font-weight: 600;
                `,
            },
        },
    },
    extendedUnitNetPriceGridItem: {
        width: 4,
    },
    extendedUnitNetPrice: {
        text: {
            weight: 700,
        },
    },
};

const CheckoutCartLineCardCondensed = ({
    cart,
    cartLine,
    editable,
    showInvalidPriceMessage,
    extendedStyles,
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(checkoutCartLineCardCondensedStyles, extendedStyles));

    return (
        <GridContainer
            {...styles.container}
            data-test-selector={`cartline_condensed_${cartLine.productId}_${cartLine.unitOfMeasure}`}
        >
            <GridItem {...styles.productImageGridItem}>
                <ProductImage product={cartLine} extendedStyles={styles.productImage} />
            </GridItem>
            <GridItem {...styles.orderLineInfoGridItem}>
                <GridItem {...styles.infoLeftColumn}>
                    <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                    <ProductPrice
                        product={cartLine}
                        currencySymbol={cart.currencySymbol}
                        showInvalidPriceMessage={showInvalidPriceMessage}
                        extendedStyles={styles.price}
                    />
                    <CartLineQuantity
                        cart={cart}
                        extendedStyles={styles.quantity}
                        editable={editable}
                        label={translate("Quantity")}
                    />
                </GridItem>
            </GridItem>
        </GridContainer>
    );
};

export default withCartLine(CheckoutCartLineCardCondensed);
